import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  Add,
  Visibility,
  Edit,
  Delete,
  Phone,
} from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import apiService from '../services/apiService';

const PassengersPage = () => {
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);

  useEffect(() => {
    fetchPassengers();
  }, []);

  const fetchPassengers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPassengers();

      if (response.success) {
        setPassengers(response.data);
        setError('');
      } else {
        setError(response.message || 'Failed to load passengers');
        setPassengers([]);
      }
    } catch (err) {
      console.error('Passengers error:', err);
      setError('Failed to load passengers. Please check your connection and try again.');
      setPassengers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPassenger = () => {
    setEditMode(false);
    setSelectedPassenger(null);
    setOpenAddDialog(true);
  };

  const handleEditPassenger = (passenger) => {
    setEditMode(true);
    setSelectedPassenger(passenger);
    setOpenAddDialog(true);
  };

  const handleSubmitPassenger = async (values, { setSubmitting }) => {
    try {
      console.log('Submitting passenger data:', { name: values.name, gender: values.gender, phone: values.phone, status: values.status });

      let response;
      if (editMode) {
        response = await apiService.updatePassenger(selectedPassenger.id, {
          name: values.name,
          gender: values.gender,
          phone: values.phone,
          status: values.status,
        });
      } else {
        response = await apiService.addPassenger({
          name: values.name,
          gender: values.gender,
          phone: values.phone,
          status: values.status,
        });
      }

      console.log('API Response:', response);

      if (response.success) {
        alert(`Passenger ${editMode ? 'updated' : 'added'} successfully`);
        setOpenAddDialog(false);
        setSelectedPassenger(null);
        setEditMode(false);
        fetchPassengers();
      } else {
        console.error('API returned error:', response);
        alert(response.message || `Failed to ${editMode ? 'update' : 'add'} passenger`);
      }
    } catch (err) {
      console.error(`${editMode ? 'Update' : 'Add'} passenger error:`, err);
      console.error('Error details:', err.response?.data || err.message);
      alert(`Failed to ${editMode ? 'update' : 'add'} passenger: ${err.response?.data?.message || err.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewPassenger = async (passenger) => {
    setSelectedPassenger(passenger);
    setOpenViewDialog(true);
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
    gender: Yup.string().required('Gender is required'),
    phone: Yup.string()
      .required('Phone number is required')
      .matches(/^\+?[1-9]\d{1,14}$/, 'Please enter a valid phone number'),
    status: Yup.string().required('Status is required'),
  });

  const handleDeletePassenger = async (passengerId) => {
    if (!window.confirm('Are you sure you want to delete this passenger? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.deletePassenger(passengerId);

      if (response.success) {
        alert('Passenger deleted successfully');
        fetchPassengers();
      } else {
        alert(response.message || 'Failed to delete passenger');
      }
    } catch (err) {
      alert('Failed to delete passenger');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: 'Active', color: 'success' },
      blocked: { label: 'Blocked', color: 'error' },
      inactive: { label: 'Inactive', color: 'default' },
    };

    const config = statusConfig[status] || statusConfig.inactive;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Passenger Name',
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.phone}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone Number',
      width: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => getStatusChip(params.row.status),
    },
    {
      field: 'totalRides',
      headerName: 'Total Rides',
      width: 120,
      valueGetter: (params) => params.row.totalRides || 0,
    },
    {
      field: 'totalSpent',
      headerName: 'Total Spent',
      width: 130,
      renderCell: (params) => `₹${Number(params.row.totalSpent || 0).toLocaleString()}`,
    },
    {
      field: 'rating',
      headerName: 'Rating',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          ⭐ {Number(params.row.rating || 5.0).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Joined',
      width: 150,
      valueGetter: (params) => params.row.created_at ? new Date(params.row.created_at).toLocaleDateString() : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleViewPassenger(params.row)}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" onClick={() => handleEditPassenger(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeletePassenger(params.row.id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const filteredPassengers = passengers.filter((passenger) =>
    passenger.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    passenger.phone.includes(searchQuery)
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Passengers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all passenger accounts
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddPassenger}>
          Add Passenger
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search by name or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <DataGrid
            rows={filteredPassengers}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* View Passenger Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Passenger Details</DialogTitle>
        <DialogContent>
          {selectedPassenger && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedPassenger.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedPassenger.phone}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Gender</Typography>
                  <Typography variant="body1">{selectedPassenger.gender || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedPassenger.status)}</Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Total Rides</Typography>
                  <Typography variant="body1">{selectedPassenger.totalRides || 0}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Total Spent</Typography>
                  <Typography variant="body1">₹{Number(selectedPassenger.totalSpent || 0).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Rating</Typography>
                  <Typography variant="body1">⭐ {Number(selectedPassenger.rating || 0.0).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Joined Date</Typography>
                  <Typography variant="body1">
                    {selectedPassenger.created_at ? new Date(selectedPassenger.created_at).toLocaleDateString() : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Passenger Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={() => {
          setOpenAddDialog(false);
          setSelectedPassenger(null);
          setEditMode(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Passenger</DialogTitle>
        <Formik
          initialValues={{
            name: selectedPassenger?.name || '',
            gender: selectedPassenger?.gender || 'Male',
            phone: selectedPassenger?.phone || '',
            status: selectedPassenger?.status || 'active',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmitPassenger}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      placeholder="Enter Full name"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        name="gender"
                        value={values.gender}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Gender"
                        error={touched.gender && Boolean(errors.gender)}
                      >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Transgender">Transgender</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="phone"
                      label="Phone Number"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                      placeholder="+919876543210"
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Status"
                        error={touched.status && Boolean(errors.status)}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="blocked">Blocked</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => {
                  setOpenAddDialog(false);
                  setSelectedPassenger(null);
                  setEditMode(false);
                }}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {editMode ? 'Update' : 'Add'} Passenger
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default PassengersPage;
